import {
  Check,
  ChevronDown,
  ChevronUp,
  ClipboardCheck,
  Download,
  FileText,
  Loader2,
  MessageSquare,
  Trash2,
  Upload
} from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Dimmer, Loader } from "semantic-ui-react";
import {
  useDeleteComment,
  useDeleteDocumentById,
  useGetCommentDataById,
  useGetDocumentUrlByKey,
  usePatchCommentData,
  usePostCommentData,
  useUploadFileDocument,
  normalizeCommentParentId
} from "../../api/noteDocument/noteDocument";
import { errorView, successMessage } from "../../helpers/ErrorHandler";
import { getTenantId } from "../../utils/cacheStorage";
import { Badge } from "../ui/common/badge";
import { Button } from "../ui/common/button";
import { Card, CardContent, CardHeader } from "../ui/common/card";
import { Textarea } from "../ui/common/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/common/tooltip";
import "./noteDocument.scss";

// Types
interface NoteAndDocumentProps {
  isDisabled?: boolean;
  parentId?: string | null;
  disableText?: boolean;
  uploadButtonView?: boolean;
  showSaveButton?: boolean;
}

type TabType = "internal" | "review" | "documents";

interface DocumentFile {
  file?: File;
  fileName?: string;
  fileType?: string;
  parentId?: string | null;
  uploadOn?: string;
  url?: string;
  key?: string;
  isNew?: boolean;
}

interface NoteFormData {
  internalNotes: string;
  reviewingNotes: string;
  supportingDocuments: DocumentFile[];
}

const NoteAndDocument = forwardRef(
  (
    {
      isDisabled = false,
      parentId = null,
      disableText = false,
      uploadButtonView = true,
      showSaveButton = false
    }: NoteAndDocumentProps,
    ref
  ) => {
    // State management
    const [accordionEnabled, setAccordionEnabled] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<TabType>("internal");
    const [downloadKey, setDownloadKey] = useState<string | null>(null);
    const [saving, setSaving] = useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const normalizedParentId = useMemo(() => normalizeCommentParentId(parentId), [parentId]);

    // API Hooks
    const { data: commentData, refetch: refetchCommentData, isFetching, isSuccess } =
      useGetCommentDataById(normalizedParentId);
    const { mutate: createComment } = usePostCommentData();
    const { mutate: updateComment } = usePatchCommentData();
    const { mutate: uploadFileData } = useUploadFileDocument();
    const { mutate: deleteComment } = useDeleteComment();
    const { mutate: deleteDocumentsById } = useDeleteDocumentById();

    // Document download handler
    const {
      data: currentDocumentData,
      status: documentUrlStatus,
      fetchStatus: documentUrlFetchStatus
    } = useGetDocumentUrlByKey(downloadKey);

    useEffect(() => {
      if (downloadKey && currentDocumentData?.signedUrl) {
        window.open(currentDocumentData.signedUrl);
        setDownloadKey(null);
      }
    }, [currentDocumentData, downloadKey]);

    // Initialize form with backend data
    const defaultValues = useMemo<NoteFormData>(
      () => ({
        internalNotes: commentData?.internalNotes || "",
        reviewingNotes: commentData?.reviewingNotes || "",
        supportingDocuments: commentData?.supportingDocuments || []
      }),
      [commentData]
    );

    // React Hook Form setup
    const {
      control,
      handleSubmit,
      reset,
      formState: { isDirty, isSubmitting },
      setValue,
      getValues
    } = useForm<NoteFormData>({
      mode: "onChange",
      defaultValues
    });

    // Watch form values
    const internalNotes = useWatch({ control, name: "internalNotes" });
    const reviewingNotes = useWatch({ control, name: "reviewingNotes" });
    const supportingDocuments = useWatch({ control, name: "supportingDocuments" });

    // Reset form when server data loads for an existing parent id.
    // Skip while parentId is missing or comment data is still loading to preserve draft notes.
    useEffect(() => {
      if (!normalizedParentId || isFetching || !isSuccess) return;

      reset({
        internalNotes: commentData?.internalNotes || "",
        reviewingNotes: commentData?.reviewingNotes || "",
        supportingDocuments: commentData?.supportingDocuments || []
      });
    }, [commentData, normalizedParentId, isFetching, isSuccess, reset]);

    // Check if there are unsaved changes
    const hasUnsavedChanges = useMemo(() => {
      return isDirty || supportingDocuments?.some((doc: DocumentFile) => doc.isNew);
    }, [isDirty, supportingDocuments]);

    // Handlers
    const handleAccordionClick = () => {
      setAccordionEnabled(prev => !prev);
    };

    const handleFileOpen = (file: DocumentFile) => {
      if (file?.isNew && file.file) {
        const url = URL.createObjectURL(file.file);
        window.open(url);
        return;
      }
      if (file.key) {
        setDownloadKey(file.key);
      }
    };

    const handleRemoveDocument = (index: number) => {
      const currentDocs = getValues("supportingDocuments");
      const deleteFile = currentDocs[index];

      if (deleteFile?.key) {
        deleteDocumentsById(deleteFile.key, {
          onSuccess: () => {
            const updatedDocs = currentDocs.filter((_, i) => i !== index);
            setValue("supportingDocuments", updatedDocs, { shouldDirty: true });
            successMessage("Document removed successfully");
          },
          onError: () => {
            errorView("Failed to remove document");
          }
        });
      } else {
        const updatedDocs = currentDocs.filter((_, i) => i !== index);
        setValue("supportingDocuments", updatedDocs, { shouldDirty: true });
      }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;

      const currentDocs = getValues("supportingDocuments") || [];
      const newFiles: DocumentFile[] = Array.from(files).map(file => ({
        file,
        parentId: normalizedParentId,
        uploadOn: new Date().toDateString(),
        isNew: true
      }));

      setValue("supportingDocuments", [...currentDocs, ...newFiles], { shouldDirty: true });
      e.target.value = "";
    };

    const uploadDocuments = async (parentId: string): Promise<DocumentFile[]> => {
      const currentDocs = getValues("supportingDocuments") || [];
      const existingDocs = currentDocs.filter((doc: DocumentFile) => !doc.isNew);
      const newDocs = currentDocs.filter((doc: DocumentFile) => doc.isNew);

      if (newDocs.length === 0) {
        return existingDocs;
      }

      const uploadedDocs: DocumentFile[] = [];

      for (const doc of newDocs) {
        if (!doc.file) continue;

        try {
          const uploadedFile = await new Promise<DocumentFile>((resolve, reject) => {
            uploadFileData(
              { ...doc, parentId: parentId || doc.parentId },
              {
                onSuccess: (data: any) =>
                  resolve({
                    fileName: doc.file!.name,
                    fileType: doc.file!.type,
                    uploadOn: doc.uploadOn,
                    url: data.url,
                    key: data.Key
                  }),
                onError: reject
              }
            );
          });
          uploadedDocs.push(uploadedFile);
        } catch (error) {
          console.error("Failed to upload document:", error);
          throw error;
        }
      }

      return [...existingDocs, ...uploadedDocs];
    };

    const resolveParentId = (overrideParentId?: string | null) =>
      normalizeCommentParentId(overrideParentId ?? parentId);

    // Validates if submission is allowed
    const validateSubmission = (overrideParentId?: string | null): boolean => {
      return Boolean(resolveParentId(overrideParentId));
    };

    // Builds the comment payload
    const buildCommentPayload = (
      formData: NoteFormData,
      uploadedDocs: DocumentFile[],
      overrideParentId?: string | null
    ) => ({
      id: commentData?._id || null,
      data: {
        internalNotes: formData.internalNotes,
        reviewingNotes: formData.reviewingNotes,
        supportingDocuments: uploadedDocs,
        tenantId: getTenantId(),
        parentId: resolveParentId(overrideParentId)!
      }
    });

    // Creates a new comment
    const createNewComment = (payload: any): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        createComment(payload, {
          onSuccess: () => {
            if (normalizedParentId) {
              void refetchCommentData();
            }
            resolve();
          },
          onError: (error: any) => {
            errorView(error?.msg || "Failed to create notes");
            reject(error);
          }
        });
      });
    };

    // Updates existing comment
    const updateExistingComment = (payload: any): Promise<void> => {
      return new Promise<void>((resolve, reject) => {
        updateComment(payload, {
          onSuccess: () => {
            if (normalizedParentId) {
              void refetchCommentData();
            }
            resolve();
          },
          onError: (error: any) => {
            errorView(error?.msg || "Failed to update notes");
            reject(error);
          }
        });
      });
    };

    // Saves comment (create or update)
    const saveComment = async (
      formData: NoteFormData,
      overrideParentId?: string | null
    ): Promise<void> => {
      const effectiveParentId = resolveParentId(overrideParentId);
      if (!effectiveParentId) return;

      const uploadedDocs = await uploadDocuments(effectiveParentId);
      const payload = buildCommentPayload(formData, uploadedDocs, effectiveParentId);

      if (commentData?._id) {
        await updateExistingComment(payload);
      } else {
        await createNewComment(payload);
      }
    };

    // Main submit handler
    const submitNotes = async (data: NoteFormData, overrideParentId?: string | null) => {
      if (!validateSubmission(overrideParentId)) return;

      setSaving(true);

      try {
        await saveComment(data, overrideParentId);
      } catch (error) {
        console.error("Save failed:", error);
      } finally {
        setSaving(false);
      }
    };

    const onSubmit = async (data: NoteFormData) => {
      await submitNotes(data);
    };

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      async saveDocumentAndNote(saveParentId: string) {
        const normalizedSaveParentId = normalizeCommentParentId(saveParentId);
        if (!normalizedSaveParentId) return;
        await submitNotes(getValues(), normalizedSaveParentId);
      },
      deleteComment() {
        if (commentData?._id) {
          deleteComment(commentData._id, {
            onSuccess: () => {
              successMessage("Comment deleted successfully");
            },
            onError: () => {
              errorView("Failed to delete comment");
            }
          });
        }
      }
    }));

    // Tab configuration
    const tabs: { key: TabType; label: string; icon: React.ReactNode }[] = [
      { key: "internal", label: "Internal Notes", icon: <MessageSquare className="tw-h-3.5 tw-w-3.5" /> },
      { key: "review", label: "Review Notes", icon: <ClipboardCheck className="tw-h-3.5 tw-w-3.5" /> },
      { key: "documents", label: "Upload Documentation", icon: <Upload className="tw-h-3.5 tw-w-3.5" /> }
    ];

    // Loading state — avoid full-screen Dimmer during save; it can trigger the side modal to close
    if (documentUrlStatus === "loading" && documentUrlFetchStatus === "fetching") {
      return (
        <Dimmer active>
          <Loader content="Loading" />
        </Dimmer>
      );
    }

    const handleSaveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      void handleSubmit(onSubmit)();
    };

    const isNotesLoading = Boolean(normalizedParentId) && isFetching && !isSuccess;

    return (
      <div>
        <Card className="tw-border-border tw-pt-0 tw-pb-0 tw-mt-1">
          <CardHeader
            className="tw-bg-secondary/50 tw-py-3.5 tw-px-3.5 tw-rounded-t-lg tw-cursor-pointer"
            onClick={handleAccordionClick}
          >
            <div className="tw-flex tw-items-center tw-justify-between">
              <div className="tw-flex tw-items-center tw-gap-2">
                <FileText className="tw-h-4 tw-w-4 tw-text-primary" />
                <span className="tw-text-sm tw-font-semibold tw-text-foreground">
                  Notes & Supporting Documentation
                </span>
                {supportingDocuments && supportingDocuments.length > 0 && (
                  <Badge variant="outline" className="tw-text-[10px] tw-h-5 tw-px-1.5">
                    {supportingDocuments.length}
                  </Badge>
                )}
              </div>
              <div className="tw-flex tw-items-center tw-gap-2">
                {accordionEnabled ? (
                  <ChevronUp className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
                ) : (
                  <ChevronDown className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
                )}
              </div>
            </div>
          </CardHeader>

          {accordionEnabled && (
            <CardContent
              className={`tw-relative tw-py-3.5 tw-px-3.5 tw-space-y-4 ${
                isDisabled ? "tw-opacity-50 tw-pointer-events-none" : ""
              }`}
            >
              {isNotesLoading && (
                <div
                  className="tw-absolute tw-inset-0 tw-z-10 tw-flex tw-items-center tw-justify-center tw-rounded-b-lg tw-bg-background/60"
                  aria-busy="true"
                  aria-live="polite"
                >
                  <Loader2 className="tw-h-6 tw-w-6 tw-animate-spin tw-text-muted-foreground" />
                </div>
              )}
              {/* Tab buttons */}
              <div className="tw-flex tw-gap-2 tw-flex-wrap">
                {tabs.map(tab => (
                  <Button
                    key={tab.key}
                    type="button"
                    onClick={e => {
                      e.preventDefault();
                      setActiveTab(tab.key);
                    }}
                    variant={activeTab === tab.key ? "default" : "outline"}
                    size="sm"
                    className={`tw-gap-1.5 tw-text-xs ${
                      activeTab === tab.key ? "tw-shadow-sm" : "hover:tw-bg-accent/50"
                    }`}
                  >
                    {tab.icon}
                    {tab.label}
                  </Button>
                ))}
              </div>

              {/* Internal Notes tab */}
              {activeTab === "internal" && (
                <div className="tw-space-y-2">
                  <Textarea
                    placeholder="Add personal notes for the team..."
                    value={internalNotes}
                    onChange={e => setValue("internalNotes", e.target.value, { shouldDirty: true })}
                    disabled={disableText || isDisabled}
                    className="tw-min-h-[80px] tw-text-sm"
                  />
                </div>
              )}

              {/* Review Notes tab */}
              {activeTab === "review" && (
                <div className="tw-space-y-2">
                  <Textarea
                    placeholder="Add notes for the reviewing process..."
                    value={reviewingNotes}
                    onChange={e =>
                      setValue("reviewingNotes", e.target.value, { shouldDirty: true })
                    }
                    disabled={disableText || isDisabled}
                    className="tw-min-h-[80px] tw-text-sm"
                  />
                </div>
              )}

              {/* Documents tab */}
              {activeTab === "documents" && (
                <div className="tw-space-y-3">
                  {supportingDocuments && supportingDocuments.length > 0 && (
                    <div className="tw-space-y-2">
                      {supportingDocuments.map((file: DocumentFile, index: number) => (
                        <div
                          key={index}
                          className="tw-flex tw-items-center tw-justify-between tw-rounded-md tw-border tw-bg-muted/30 tw-p-3"
                        >
                          <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                            <FileText className="tw-h-4 tw-w-4 tw-text-primary tw-shrink-0" />
                            <div className="tw-min-w-0">
                              <p className="tw-text-sm tw-font-medium tw-truncate">
                                {file?.file?.name || file.fileName}
                              </p>
                              <p className="tw-text-[10px] tw-text-muted-foreground">{file.uploadOn}</p>
                            </div>
                            {file.isNew && (
                              <Badge variant="secondary" className="tw-text-[10px] tw-h-5 tw-px-1.5">
                                New
                              </Badge>
                            )}
                          </div>
                          <div className="tw-flex tw-gap-1 tw-shrink-0">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="tw-h-7 tw-w-7"
                                    onClick={() => handleFileOpen(file)}
                                    disabled={isDisabled}
                                  >
                                    <Download className="tw-h-3 tw-w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Download</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="tw-h-7 tw-w-7 tw-text-destructive hover:tw-text-destructive"
                                    onClick={() => handleRemoveDocument(index)}
                                    disabled={isDisabled}
                                  >
                                    <Trash2 className="tw-h-3 tw-w-3" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>Remove</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {uploadButtonView && (
                    <div
                      onClick={() => !isDisabled && fileInputRef.current?.click()}
                      className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-dashed tw-border-border tw-p-6 tw-transition-colors ${
                        isDisabled
                          ? "tw-opacity-50 tw-cursor-not-allowed"
                          : "hover:tw-border-primary/50 tw-cursor-pointer hover:tw-bg-accent/20"
                      }`}
                    >
                      <Upload className="tw-h-8 tw-w-8 tw-text-muted-foreground tw-mb-2" />
                      <p className="tw-text-sm tw-font-medium tw-text-foreground">
                        Click to upload documentation
                      </p>
                      <p className="tw-text-xs tw-text-muted-foreground tw-mt-1">
                        PDF, DOCX, XLSX, images up to 10MB
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
                        className="tw-hidden"
                        onChange={handleFileSelect}
                        disabled={isDisabled}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Single Dynamic Save Button */}
              {showSaveButton && !isDisabled && (
                <div className="tw-flex tw-justify-end tw-pt-2 tw-border-t tw-border-border">
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSaveClick}
                    disabled={saving || isSubmitting || !normalizedParentId || !hasUnsavedChanges}
                    className="tw-text-xs tw-gap-1.5"
                  >
                    <Check className="tw-h-3.5 tw-w-3.5" />
                    {saving || isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    );
  }
);

export default NoteAndDocument;
